import { BlurEvent, Layout, Textbox } from "@mcleod/components";
import { StringUtil } from "@mcleod/core";
import { CarrierScorecardProfileDetail } from "./CarrierScorecardProfileDetail";

export class CarrierScorecardUtil {
    static async configureLayoutForMetric(parent: CarrierScorecardProfileDetail) {
        const layouts = [
            parent.layoutEDIAccuracyDetail,
            parent.layoutEDIResponseDetail,
            parent.layoutCarrierCancellationsDetail,
            parent.layoutPaymentMethodDetail,
            parent.layoutDateRangeMoveWeightDetail,
            parent.layoutClaimsDetail,
            parent.layoutBrokerRatingDetail,
            parent.layoutCheckCallComplianceDetail,
            parent.layoutCarrierPayDetail,
            parent.layoutOnTimeDetail,
            parent.layoutNumberOfMovesDetail
        ];
        await Promise.all(layouts.map(layout => layout.ensureLoaded()));
        for (const layout of layouts) {
            layout.forEveryChildComponent((component) => {
                parent.mainDataSource.removeBoundComponent(component);
                if (component.required) {
                    component.required = false;
                }
            });
            layout.tabsetScoringDetails.collapseAll();
            layout.visible = false;
        }
        const performanceMetric = parent.mainDataSource.activeRow?.get("performance_metric");

        const metricLayout = this.getLayoutForMetric(parent, performanceMetric);
        if (metricLayout) {
            metricLayout.ensureLoaded()
                .then(() => {
                    metricLayout.visible = true;
                    metricLayout.forEveryChildComponent((component) => {
                        parent.mainDataSource.addBoundComponent(component);
                        component.displayData(parent.mainDataSource.activeRow, null, 0);
                    });

                    const textboxWeight = metricLayout.findComponentById("textboxWeight") as Textbox;
                    if (textboxWeight) {
                        textboxWeight.addBlurListener((event: BlurEvent) => {
                            if (event.changedWhileFocused) {
                                CarrierScorecardUtil.syncWeightSuffix(textboxWeight, metricLayout);
                            }
                        });
                        CarrierScorecardUtil.syncWeightSuffix(textboxWeight, metricLayout);
                    }
                });
        }
        return metricLayout;
    }

    private static getLayoutForMetric(layout: CarrierScorecardProfileDetail, performanceMetric: string): Layout {
        switch (performanceMetric) {
            case "EDIACCY":
                const layoutEDIAccuracyDetail = layout.layoutEDIAccuracyDetail;
                layoutEDIAccuracyDetail.textboxStartOfPeriod.required =
                    layoutEDIAccuracyDetail.textboxEndOfPeriod.required =
                    layoutEDIAccuracyDetail.textboxMinimumTransactions.required =
                    layoutEDIAccuracyDetail.textboxWeight.required =
                    layoutEDIAccuracyDetail.textboxMeasure.required = true;
                return layoutEDIAccuracyDetail;
            case "204RESPS":
                const layoutEDIResponseDetail = layout.layoutEDIResponseDetail;
                layoutEDIResponseDetail.textboxStartOfPeriod.required =
                    layoutEDIResponseDetail.textboxEndOfPeriod.required =
                    layoutEDIResponseDetail.textboxMinimumTransactions.required =
                    layoutEDIResponseDetail.textboxWeight.required =
                    layoutEDIResponseDetail.textboxMeasure.required = true;
                return layoutEDIResponseDetail;
            case "CANCELS":
                const layoutCarrierCancellationsDetail = layout.layoutCarrierCancellationsDetail;
                layoutCarrierCancellationsDetail.textboxStartOfPeriod.required =
                    layoutCarrierCancellationsDetail.textboxEndOfPeriod.required =
                    layoutCarrierCancellationsDetail.textboxMinimumRecords.required =
                    layoutCarrierCancellationsDetail.textboxWeight.required = true;
                return layoutCarrierCancellationsDetail;
            case "PAYMTHD":
                const layoutPaymentMethodDetail = layout.layoutPaymentMethodDetail;
                layoutPaymentMethodDetail.textboxMeasure.required = true;
                return layoutPaymentMethodDetail;
            case "BRTRACK":
            case "RATECONF":
            case "OFFACPTD":
            case "PAPRWORK":
                const detail = layout.layoutDateRangeMoveWeightDetail;
                detail.panelBrTrackScoreDetails.visible = "BRTRACK" === performanceMetric;
                detail.panelRateConfScoreDetails.visible = "RATECONF" === performanceMetric;
                detail.panelOffAcptdScoreDetails.visible = "OFFACPTD" === performanceMetric;
                detail.panelPaprworkScoreDetails.visible = "PAPRWORK" === performanceMetric;
                detail.textboxStartOfPeriod.required =
                    detail.textboxEndOfPeriod.required =
                    detail.textboxMinimumRecords.required =
                    detail.textboxWeight.required = true;
                return detail;
            case "CLAIMS":
                const layoutClaimsDetail = layout.layoutClaimsDetail;
                layoutClaimsDetail.textboxStartOfPeriod.required =
                    layoutClaimsDetail.textboxEndOfPeriod.required =
                    layoutClaimsDetail.textboxMinimumRecords.required =
                    layoutClaimsDetail.textboxWeight.required = true;
                return layoutClaimsDetail;
            case "BROKRATE":
                const layoutBrokerRatingDetail = layout.layoutBrokerRatingDetail;
                layoutBrokerRatingDetail.textboxStartOfPeriod.required =
                    layoutBrokerRatingDetail.textboxEndOfPeriod.required =
                    layoutBrokerRatingDetail.textboxMinimumRecords.required =
                    layoutBrokerRatingDetail.textboxWeight.required =
                    layoutBrokerRatingDetail.textboxPrompt.required = true;
                return layoutBrokerRatingDetail;
            case "CHKCALLS":
                const layoutCheckCallComplianceDetail = layout.layoutCheckCallComplianceDetail;
                layoutCheckCallComplianceDetail.textboxStartOfPeriod.required =
                    layoutCheckCallComplianceDetail.textboxEndOfPeriod.required =
                    layoutCheckCallComplianceDetail.textboxMinimumRecords.required =
                    layoutCheckCallComplianceDetail.textboxWeight.required = true;
                return layoutCheckCallComplianceDetail;
            case "CARRPAY":
                const layoutCarrierPayDetail = layout.layoutCarrierPayDetail;
                layoutCarrierPayDetail.textboxStartOfPeriod.required =
                    layoutCarrierPayDetail.textboxEndOfPeriod.required =
                    layoutCarrierPayDetail.textboxMinimumRecords.required =
                    layoutCarrierPayDetail.textboxWeight.required =
                    layoutCarrierPayDetail.textboxMeasure.required = true;
                return layoutCarrierPayDetail;
            case "ONTIME":
                const layoutOnTimeDetail = layout.layoutOnTimeDetail;
                layoutOnTimeDetail.textboxStartOfPeriod.required =
                    layoutOnTimeDetail.textboxEndOfPeriod.required =
                    layoutOnTimeDetail.textboxMinimumRecords.required =
                    layoutOnTimeDetail.textboxWeight.required =
                    layoutOnTimeDetail.textboxMeasure.required = true;
                return layoutOnTimeDetail;
            case "NUMMOVES":
                const layoutNumberOfMovesDetail = layout.layoutNumberOfMovesDetail;
                layoutNumberOfMovesDetail.textboxStartOfPeriod.required =
                    layoutNumberOfMovesDetail.textboxEndOfPeriod.required =
                    layoutNumberOfMovesDetail.textboxMinimumRecords.required =
                    layoutNumberOfMovesDetail.textboxWeight.required =
                    layoutNumberOfMovesDetail.textboxMeasure.required = true;
                return layoutNumberOfMovesDetail;
            default:
                return null;
        }
    }

    static syncWeightSuffix(textboxWeight: Textbox, layout: Layout): void {
        const weight = textboxWeight.text;
        const isNotEmpty = !(StringUtil.isEmptyString(weight) || parseFloat(weight) == 0);
        const isPositive = isNotEmpty && parseFloat(weight) > 0;
        const labelAdded = layout.findComponentById("labelAdded");
        const labelSubtracted = layout.findComponentById("labelSubtracted");
        if (labelAdded && labelSubtracted) {
            labelAdded.visible = textboxWeight.visible && isNotEmpty && isPositive;
            labelSubtracted.visible = textboxWeight.visible && isNotEmpty && !isPositive;
        }
    }
}
